import React, { FunctionComponent, useMemo, useState } from "react";
import { ACard, AChartX, AChartXProps, XAxis, YAxis } from "@atiautomacao/ati-ui-library";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import { Skeleton, Stack, TextField, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { endOfDay, format, isWithinInterval, startOfDay } from "date-fns";
import { getChartXOptions } from "../../../Utils/ChartXOption";
import { LocalDateTimeFormatISO } from "../../../Utils/DateFormatPatternUtils";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import { useAppSelector } from "../../../Config/Hooks";
import { RootState } from "../../../Config/Store";
import { useBessPower } from "./BessReactQuery";
import { BESS_DATA_TYPE, BessChartSeries } from "./BessChartTypes";
import { computePowerYAxisRange, hasPoints, toTimeSeriesData } from "./BessChartUtils";

const ACTIVE_COLOR = "#2D5DE7";   // Potência Ativa (kW)
const REACTIVE_COLOR = "#FFA000"; // Potência Reativa (kVAr)
const EMPTY_SERIES: Array<BessChartSeries> = [];

const seriesConfig = (s: BessChartSeries): { label: string; color: string; area: string; unit: string } => {
    if (s.dataTypeId === BESS_DATA_TYPE.ACTIVE_POWER) {
        const unit = s.unit || "kW";
        return { label: `Potência Ativa (${unit})`, color: ACTIVE_COLOR, area: "rgba(45, 93, 231, 0.15)", unit };
    }
    if (s.dataTypeId === BESS_DATA_TYPE.REACTIVE_POWER) {
        const unit = s.unit || "kVAr";
        return { label: `Potência Reativa (${unit})`, color: REACTIVE_COLOR, area: "rgba(255, 160, 0, 0.15)", unit };
    }
    return { label: s.dataTypeName, color: "#8c8c8c", area: "rgba(140, 140, 140, 0.15)", unit: s.unit || "" };
};

const BessPowerChart: FunctionComponent<{ equipmentId: number }> = ({ equipmentId }) => {
    const theme = useTheme();
    const openSubMenu = useAppSelector((state: RootState) => state.layout.openSubMenu);
    const [date, setDate] = useState<Date>(startOfDay(new Date()));

    const { data, isLoading } = useBessPower(equipmentId, date);

    const series = data?.data ?? EMPTY_SERIES;


    // @ts-ignore
    const option: AChartXProps["option"] = useMemo(() => {
        const range = computePowerYAxisRange(series);

        const xAxis: XAxis = {
            type: "time",
            boundaryGap: false,
            axisLabel: {
                // @ts-ignore - echarts entrega timestamp
                formatter: (value: number) => format(new Date(value), "HH:mm:ss"),
            },
        };

        // Eixo Y único; suporta valores positivos e negativos; zero sempre visível (SGD-3676 Regra 5).
        const yAxis: YAxis = {
            type: "value",
            min: range.min,
            max: range.max,
        };

        const chartSeries: any[] = series.map((s) => {
            const cfg = seriesConfig(s);
            return {
                name: cfg.label,
                type: "line" as const,
                smooth: false,        // sem interpolação (SGD-3676 Regra 5c)
                connectNulls: false,
                showSymbol: false,
                symbol: "circle",
                yAxisIndex: 0,
                lineStyle: { width: 2, color: cfg.color },
                itemStyle: { color: cfg.color },
                areaStyle: { color: cfg.area },
                tooltip: { valueFormatter: (v: any) => (v != null ? `${v} ${cfg.unit}`.trim() : "-") },
                data: toTimeSeriesData(s),
            };
        });

        return {
            ...getChartXOptions({ name: `bess-power-${equipmentId}-`, date }),
            legend: {
                show: true,
                bottom: 2,
                left: "center",
                data: chartSeries.map((s) => s.name),
            },
            grid: { containLabel: true, top: 15, bottom: 40, right: 15, left: 15 },
            toolbox: {
                feature: {
                    saveAsImage: { show: true },
                    dataZoom: { yAxisIndex: "none" },
                    restore: { show: true },
                },
            },
            dataZoom: [{ type: "inside", throttle: 50 }],
            xAxis: xAxis,
            yAxis: yAxis,
            series: chartSeries,
        };
    }, [series, date, equipmentId]);

    let content: JSX.Element;
    if (isLoading) {
        content = <Skeleton animation="wave" height={250} variant="rounded" width={"100%"} />;
    } else if (hasPoints(series)) {
        content = <AChartX height={250} option={option} theme={theme.palette.mode} />;
    } else {
        content = <DataNotFound />;
    }

    return (
        <ACard
            key={`bess-power-chart-${openSubMenu}`}
            ariaLabel={"bess-power-chart"}
            title={"Potência Ativa e Reativa (kW e kVAr)"}
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: OfflineBoltOutlinedIcon,
                iconTitleStyle: { color: "#2D5DE7", fontSize: 28 },
            }}
            headerControlsPosition={"header"}
            styleProps={{
                cardStyle: { height: "auto" },
                contentStyle: { padding: "0 16px 16px", alignItems: "center" },
                titleStyle: { alignItems: "center", display: "flex" },
                subTitleStyle: { display: "none" },
                headerStyle: { flexDirection: "row" },
                headerActionsStyle: { alignItems: "center", display: "flex" },
            }}
            headerControls={
                <Stack direction="row" sx={{ alignItems: "center", display: "flex", padding: "8px" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data"
                            disableFuture
                            value={date}
                            inputFormat={"DD/MM/YYYY"}
                            renderInput={(params) => (
                                <TextField
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", height: "30px", width: "155px" } }}
                                    {...params}
                                />
                            )}
                            onChange={(newValue) => {
                                if (newValue && isWithinInterval(new Date(newValue), { start: new Date("1980-01-01"), end: endOfDay(new Date()) })) {
                                    setDate(startOfDay(new Date(LocalDateTimeFormatISO(newValue))));
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Stack>
            }
        >
            {content}
        </ACard>
    );
};

export default BessPowerChart;
